import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL || 'https://api.gammaqc.com';

// Token helpers — localStorage fallback when cookies are blocked
const TOKEN_KEY = 'fio_session';
const storeToken = (token) => { if (token) localStorage.setItem(TOKEN_KEY, token); };
const getToken = () => localStorage.getItem(TOKEN_KEY);
const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Authenticated fetch — sends token as both cookie AND bearer header
const authFetch = (url, opts = {}) => {
  const token = getToken();
  const headers = { ...(opts.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...opts, credentials: 'include', headers });
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const res = await authFetch(`${API}/api/auth/me`);
      if (res.ok) {
        const userData = await res.json();
        // Re-hydrate the localStorage token from the authenticated session so the
        // ~12 pages that read localStorage['fio_session'] directly work even when
        // the login was cookie-only (e.g. Google OAuth). Fixes "Sign in" on
        // Scorecard/Autopsy/Twin/etc. for a genuinely-authenticated user.
        if (userData?.session_token) storeToken(userData.session_token);
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        clearToken();
      }
    } catch {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Login failed');
    storeToken(data.session_token);
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const register = async (name, email, password) => {
    const res = await fetch(`${API}/api/auth/register`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    storeToken(data.session_token);
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  // Sovereign Google OAuth — redirects to the JCAM backend's /api/auth/google/login,
  // which sets a CSRF state cookie and 302s the browser to accounts.google.com.
  // On return, the backend's /api/auth/google/callback issues the session and
  // bounces the user back to the frontend.
  const loginWithGoogle = () => {
    window.location.href = `${API}/api/auth/google/login`;
  };

  const logout = async () => {
    try {
      await authFetch(`${API}/api/auth/logout`, { method: 'POST' });
    } catch {}
    clearToken();
    setUser(null);
    setIsAuthenticated(false);
  };

  const hasActiveSubscription = () => {
    // Two paths, both authoritative — accept either to prevent
    // tier-vs-subscriptions desync from gating real paying users:
    //   1. has_active_subscription boolean from /api/auth/me (set
    //      when db.subscriptions has an active/trialing row)
    //   2. user.tier in {pro, quant, inst, sovereign} — this is the
    //      canonical field that the cockpit + daily-cap gate read
    //      (per the 2026-06-13 backend fix). Manual tier flips on
    //      db.users set this even if db.subscriptions wasn't synced,
    //      and stale localStorage user objects from before a flip
    //      should still respect the tier field once /api/auth/me
    //      refreshes them.
    if (user?.has_active_subscription === true) return true;
    const tier = (user?.tier || user?.subscription_tier || '').toLowerCase();
    return ['pro', 'quant', 'inst', 'sovereign'].includes(tier);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, isAuthenticated,
      login, register, loginWithGoogle, logout,
      hasActiveSubscription, checkAuth, authFetch,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
